import * as React from "react";
import { Box, Card, CardContent, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { dayjs } from "@/lib/dayjs";
import { TimeseriesGranularity, TimeseriesMetric, useCampaignTimeseries } from "@/hooks/api/use-campaigns";
import { NoSsr } from "@/components/core/no-ssr";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";

interface CampaignTrendCardProps {
	campaignId: string;
	metric: TimeseriesMetric;
	title: string;
	subtitle: string;
	/** Suffix appended to the tooltip value, e.g. "%" for penetration. */
	valueSuffix?: string;
}

const formatPeriod = (period: string, granularity: TimeseriesGranularity) => {
	const d = dayjs(period);
	if (!d.isValid()) return period;
	return granularity === "hour" ? d.format("HH:mm") : d.format("MMM D");
};

export function CampaignTrendCard({
	campaignId,
	metric,
	title,
	subtitle,
	valueSuffix = "",
}: CampaignTrendCardProps): React.JSX.Element {
	const [granularity, setGranularity] = React.useState<TimeseriesGranularity>("day");

	const { data, isLoading } = useCampaignTimeseries(campaignId, metric, granularity);

	const chartData = React.useMemo(
		() =>
			(data ?? []).map((point) => ({
				label: formatPeriod(point.period, granularity),
				value: point.value,
			})),
		[data, granularity]
	);

	return (
		<Card sx={{ height: "100%" }}>
			<CardContent sx={{ p: 2, pb: "12px !important" }}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
					<Box>
						<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.25 }}>
							{title}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{subtitle} · {granularity === "hour" ? "hourly" : "daily"}
						</Typography>
					</Box>
					<ToggleButtonGroup
						exclusive
						size="small"
						value={granularity}
						onChange={(_event, value) => {
							if (value) setGranularity(value as TimeseriesGranularity);
						}}
					>
						<ToggleButton value="day" sx={{ px: 1.5, py: 0.25, textTransform: "none" }}>
							Day
						</ToggleButton>
						<ToggleButton value="hour" sx={{ px: 1.5, py: 0.25, textTransform: "none" }}>
							Hour
						</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				{isLoading ? (
					<CustomSkeleton variant="rounded" width="100%" height={200} />
				) : chartData.length === 0 ? (
					<Box
						sx={{
							height: 200,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Typography variant="caption" color="text.secondary">
							No data for this period
						</Typography>
					</Box>
				) : (
					<NoSsr fallback={<Box sx={{ height: 200 }} />}>
						<ResponsiveContainer height={200} width="100%">
							<BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
								<CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--mui-palette-divider)" />
								<XAxis
									axisLine={false}
									tickLine={false}
									dataKey="label"
									tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
									interval="preserveStartEnd"
								/>
								<YAxis
									axisLine={false}
									tickLine={false}
									tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
									allowDecimals={false}
								/>
								<Tooltip
									cursor={{ fill: "var(--mui-palette-action-hover)" }}
									formatter={(value: number) => [`${value}${valueSuffix}`, title]}
									contentStyle={{
										backgroundColor: "var(--mui-palette-background-paper)",
										border: "1px solid var(--mui-palette-divider)",
										borderRadius: 8,
										boxShadow: "var(--mui-shadows-3)",
									}}
								/>
								<Bar
									dataKey="value"
									fill="var(--mui-palette-primary-main)"
									radius={[4, 4, 0, 0]}
									maxBarSize={28}
									animationDuration={300}
								/>
							</BarChart>
						</ResponsiveContainer>
					</NoSsr>
				)}
			</CardContent>
		</Card>
	);
}
